// Clean AuthContext - fixed redirect loop
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface User {
  id: number;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  role: 'user' | 'admin';
  timezone?: string;
  admin_panel_timezone?: string | null;
  system_timezone?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (firstName: string, lastName: string, email: string, password: string, timezone: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Helper functions for token management
  const getToken = (): string | null => localStorage.getItem('token');
  const setToken = (token: string): void => localStorage.setItem('token', token);
  const removeToken = (): void => localStorage.removeItem('token');
  const setCachedUser = (data: User | null): void => {
    if (data) {
      localStorage.setItem('user', JSON.stringify(data));
    } else {
      localStorage.removeItem('user');
    }
  };

  // Check auth when component mounts
  useEffect(() => {
    let mounted = true;
    
    const validateAuth = async () => {
      // First check if token exists in localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        if (mounted) {
          setUser(null);
          setIsLoading(false);
        }
        return;
      }
      
      try {
        // Use the api instance with interceptors for consistent auth handling
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Handle different response structures
        const userData = response.data?.data?.user || response.data?.data || response.data?.user || response.data;

        
        if (mounted && userData) {
          const normalizedRole = typeof userData.role === 'string' ? userData.role.toLowerCase() : userData.role;
          const normalizedUser = { ...userData, role: normalizedRole };
          setUser(normalizedUser);
          setCachedUser(normalizedUser);
        }
      } catch (error) {
        // Only remove token on 401/403 (authentication errors)
        if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
          localStorage.removeItem('token');
          setCachedUser(null);
          if (mounted) setUser(null);
        } else if (mounted) {
          const cached = localStorage.getItem('user');
          if (cached) {
            try {
              const parsed = JSON.parse(cached);
              const normalizedRole = typeof parsed.role === 'string' ? parsed.role.toLowerCase() : parsed.role;
              setUser({ ...parsed, role: normalizedRole });
            } catch {
              // ignore invalid cache
            }
          }
        }
        // For other errors (network, server), keep the token - it might be a temporary issue
      }
      
      if (mounted) {
        setIsLoading(false);
      }
    };
    
    // Small delay to ensure localStorage is accessible
    setTimeout(validateAuth, 0);
    
    return () => { 
      mounted = false; 
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    
    // Server now returns: { data: { token, user: ... } }
    const { token, user: userData } = response.data.data || response.data;
    
    if (!token || !userData) {
      throw new Error('Invalid server response');
    }
    
    setToken(token);
    const normalizedRole = typeof userData.role === 'string' ? userData.role.toLowerCase() : userData.role;
    const normalizedUser = { ...userData, role: normalizedRole };
    setUser(normalizedUser);
    setCachedUser(normalizedUser);
    
    if (userData.role === 'admin') {
      navigate('/admin', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const register = useCallback(async (firstName: string, lastName: string, email: string, password: string, timezone: string) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        firstName, lastName, email, password, timezone
      });
      
      // Server now returns: { data: { token, user: ... } }
      const { token, user: userData } = response.data.data || response.data;
      
      if (!token || !userData) {
        throw new Error('Invalid server response');
      }
      
      setToken(token);
      const normalizedRole = typeof userData.role === 'string' ? userData.role.toLowerCase() : userData.role;
      const normalizedUser = { ...userData, role: normalizedRole };
      setUser(normalizedUser);
      setCachedUser(normalizedUser);
      navigate('/dashboard', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }

    const response = await axios.get(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const userData = response.data?.data?.user || response.data?.data || response.data?.user || response.data;
    if (userData) {
      const normalizedRole = typeof userData.role === 'string' ? userData.role.toLowerCase() : userData.role;
      const normalizedUser = { ...userData, role: normalizedRole };
      setUser(normalizedUser);
      setCachedUser(normalizedUser);
    }
  }, []);

  const logout = useCallback(() => {
    removeToken();
    setCachedUser(null);
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isLoading,
    login,
    register,
    refreshUser,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
